/* eslint-disable react/destructuring-assignment */
import ContactInfoComponent from '@components/cms-components/contact-info/contact-info.view';
import HeaderComponent from '@components/cms-components/header';
import PricingComponent from '@components/cms-components/pricing';
import StackedHeaderComponent from '@components/cms-components/stacked-header/stacked-header.data';
import StackedImagesComponent from '@components/cms-components/stacked-images/stacked-images.data';
import StylistList from '@components/cms-components/stylist-list';
import TextBlockComponent from '@components/cms-components/text-block';
import TwoColumnComponent from '@components/cms-components/two-column';
import CodeBlock from '@components/code-block';
import ComponentBlock from '@components/component-block';
import Container from '@components/container';
import Img from '@components/img';
import PageIndexComponent from '@components/page-index';
import RichTextComponent from '@components/rich-text';
import type { ReactElement } from 'react';
import React, { Fragment } from 'react';
import type { BodyContent } from 'types/components/body-content';
import { ComponentTypes } from 'types/components/component-types';
import type SiteContext from 'types/site-context';

const TAB_INDENT = 2;
const EMPTY = 0;

interface Props {
  root?: boolean | undefined;
  context: SiteContext;
  children?: BodyContent | BodyContent[] | undefined;
}

function ComponentRenderer({
  context,
  children,
  root = false,
}: Readonly<Props>): ReactElement | null {
  if (typeof children === 'undefined') {
    return null;
  }

  if (typeof children === 'string') {
    return <>{children}</>;
  }

  if (Array.isArray(children)) {
    if (children.length === EMPTY) {
      return null;
    }

    return (
      <>
        {children.map((child, index) => (
          <ComponentRenderer
            context={context}
            key={
              typeof child === 'object' && '_key' in child ? child._key : index
            }
          >
            {child}
          </ComponentRenderer>
        ))}
      </>
    );
  }

  const props = { ...children, context };

  switch (props._type) {
    case ComponentTypes.ContactInfo: {
      return <ContactInfoComponent {...props} />;
    }
    case ComponentTypes.Header: {
      return (
        <ComponentBlock
          backgroundImage={props.backgroundImage}
          fullscreen={props.fullscreen}
          key={props._key}
          theme={props.theme}
        >
          <HeaderComponent {...props} />
        </ComponentBlock>
      );
    }
    case ComponentTypes.StackedHeader: {
      return (
        <ComponentBlock fullWidth key={props._key} theme={props.theme}>
          <StackedHeaderComponent {...props} />
        </ComponentBlock>
      );
    }

    case ComponentTypes.StackedImages: {
      return <StackedImagesComponent key={props._key} {...props} />;
    }

    case ComponentTypes.Image: {
      return (
        <Img
          alt=""
          imageSource={props.asset}
          priority={props.priority}
          sizes={props.sizes}
        />
      );
    }
    case ComponentTypes.TextBlock: {
      return (
        <ComponentBlock key={props._key} theme={props.theme}>
          <TextBlockComponent {...props} />
        </ComponentBlock>
      );
    }
    case ComponentTypes.TwoColumn: {
      return (
        <ComponentBlock key={props._key} theme={props.theme}>
          <TwoColumnComponent {...props} />
        </ComponentBlock>
      );
    }
    case 'pageIndex': {
      return (
        <ComponentBlock key={props._key}>
          <PageIndexComponent {...props} />
        </ComponentBlock>
      );
    }

    case ComponentTypes.Pricing: {
      return (
        <ComponentBlock key={props._key} size="x-small">
          <PricingComponent {...props} />
        </ComponentBlock>
      );
    }

    case 'richText': {
      const Wrapper = root ? Container : Fragment;
      return (
        <Wrapper key={props._key}>
          <RichTextComponent value={props} />
        </Wrapper>
      );
    }
    case 'code': {
      return (
        <Container key={props._key} style={{ marginBottom: '1rem' }}>
          <CodeBlock
            code={props.code}
            filename={props.filename}
            language={props.language}
          />
        </Container>
      );
    }
    case ComponentTypes.StylistList: {
      return (
        <ComponentBlock key={props._key}>
          <StylistList {...props} />
        </ComponentBlock>
      );
    }
    default: {
      return (
        <div>
          Unknown component: {JSON.stringify(children, null, TAB_INDENT)}
        </div>
      );
    }
  }
}

export default ComponentRenderer;
